'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Edit2,
  GripVertical,
  Plus as PlusIcon,
  Copy,
  Settings,
} from 'lucide-react';
import type {
  Trigger,
  Condition,
  Action,
  TriggerType,
  ActionType,
  OperatorType,
} from './automationTypes';
import { getTriggerConfig, getActionConfig } from './automationConstants';

// ==================== ActionItem ====================

interface ActionItemProps {
  action: Action;
  index: number;
  onRemove: (id: string) => void;
  onReorder: (from: number, to: number) => void;
  onEdit: (action: Action) => void;
}

export const ActionItem: React.FC<ActionItemProps> = ({ action, index: _index, onRemove, onReorder: _onReorder, onEdit }) => {
  const { t } = useTranslation();
  const actionConfig = useMemo(() => getActionConfig(t), [t]);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-200/60 hover:border-emerald-300 transition-colors"
      style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
    >
      <GripVertical size={14} className="text-gray-400 cursor-grab active:cursor-grabbing" />
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          {actionConfig[action.type]?.icon}
          <span className="font-semibold text-gray-900 text-sm">{actionConfig[action.type]?.label}</span>
        </div>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {action.config?.description || t('auto_no_desc', 'No description')}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(action)}
          className="p-2 hover:bg-emerald-50 rounded-lg transition-all duration-200 text-gray-600 hover:text-emerald-600"
          title={t('auto_edit_action', 'Edit action')}
          aria-label={t('auto_edit_action', 'Edit action')}
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => onRemove(action.id)}
          className="p-2 hover:bg-red-50 rounded-lg transition-all duration-200 text-gray-600 hover:text-red-600"
          title={t('auto_remove_action', 'Remove action')}
          aria-label={t('auto_remove_action', 'Remove action')}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </motion.div>
  );
};

// ==================== TriggerConfigurator ====================

interface TriggerConfiguratorProps {
  trigger: Trigger | null;
  onChange: (trigger: Trigger) => void;
}

export const TriggerConfigurator: React.FC<TriggerConfiguratorProps> = ({ trigger, onChange }) => {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const triggerConfig = useMemo(() => getTriggerConfig(t), [t]);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="triggerType" className="block text-sm font-semibold text-gray-900 mb-2">{t('auto_trigger_type', 'Trigger Type')}</label>
        <select
          id="triggerType"
          value={trigger?.type || 'manual'}
          onChange={(e) =>
            onChange({
              type: e.target.value as TriggerType,
              config: {},
            })
          }
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white"
        >
          {Object.entries(triggerConfig).map(([key, config]) => (
            <option key={key} value={key}>
              {config.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-2">{triggerConfig[trigger?.type as TriggerType]?.description}</p>
      </div>

      <AnimatePresence>
        {trigger?.type === 'engagement_threshold' && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-3 pt-2">
            <div>
              <label htmlFor="metric" className="block text-sm font-semibold text-gray-900 mb-2">{t('auto_metric', 'Metric')}</label>
              <select
                id="metric"
                value={trigger?.config?.metric || 'likes'}
                onChange={(e) =>
                  onChange({
                    ...trigger!,
                    config: { ...trigger!.config, metric: e.target.value },
                  })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white"
              >
                <option value="likes">{t('auto_likes', 'Likes')}</option>
                <option value="comments">{t('auto_comments', 'Comments')}</option>
                <option value="shares">{t('auto_shares', 'Shares')}</option>
                <option value="views">{t('auto_views', 'Views')}</option>
              </select>
            </div>
            <div>
              <label htmlFor="thresholdValue" className="block text-sm font-semibold text-gray-900 mb-2">{t('auto_threshold', 'Threshold Value')}</label>
              <input
                id="thresholdValue"
                type="number"
                value={trigger?.config?.value || 0}
                onChange={(e) =>
                  onChange({
                    ...trigger!,
                    config: { ...trigger!.config, value: parseInt(e.target.value) },
                  })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white"
                placeholder={t('auto_enter_threshold', 'Enter threshold value')}
              />
            </div>
          </motion.div>
        )}

        {trigger?.type === 'schedule' && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-3 pt-2">
            <div>
              <label htmlFor="cronExpression" className="block text-sm font-semibold text-gray-900 mb-2">{t('auto_cron', 'Cron Expression')}</label>
              <input
                id="cronExpression"
                type="text"
                value={trigger?.config?.cron || '0 9 * * *'}
                onChange={(e) =>
                  onChange({
                    ...trigger!,
                    config: { ...trigger!.config, cron: e.target.value },
                  })
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white font-mono text-sm"
                placeholder="0 9 * * *"
              />
              <p className="text-xs text-gray-500 mt-2">{t('auto_cron_format', 'Format: minute hour day month weekday')}</p>
            </div>
          </motion.div>
        )}

        {trigger?.type === 'webhook' && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-3 pt-2">
            <div>
              <label htmlFor="webhookUrl" className="block text-sm font-semibold text-gray-900 mb-2">{t('auto_webhook_url', 'Webhook URL')}</label>
              <div className="flex gap-2">
                <input
                  id="webhookUrl"
                  type="text"
                  readOnly
                  value={trigger?.config?.webhookUrl || `https://api.plugd.app/webhook/${Math.random().toString(36).slice(2)}`}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(trigger?.config?.webhookUrl || '');
                    toast.success(t('auto_copied', 'Copied to clipboard'));
                  }}
                  className="px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all duration-200 text-emerald-700 font-medium"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-sm text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 transition-colors"
      >
        <Settings size={14} />
        {showAdvanced ? 'Hide' : 'Show'} Advanced Options
      </button>
    </div>
  );
};

// ==================== ConditionBuilder ====================

interface ConditionBuilderProps {
  conditions: Condition[];
  onChange: (conditions: Condition[]) => void;
}

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({ conditions, onChange }) => {
  const { t } = useTranslation();
  const addCondition = useCallback(() => {
    onChange([
      ...conditions,
      {
        id: Math.random().toString(36).slice(2),
        field: 'status',
        operator: 'equals',
        value: '',
      },
    ]);
  }, [conditions, onChange]);

  const updateCondition = useCallback(
    (id: string, updates: Partial<Condition>) => {
      onChange(
        conditions.map((c) => (c.id === id ? { ...c, ...updates } : c))
      );
    },
    [conditions, onChange]
  );

  const removeCondition = useCallback(
    (id: string) => {
      onChange(conditions.filter((c) => c.id !== id));
    },
    [conditions, onChange]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="block text-sm font-semibold text-gray-900">{t('auto_conditions', 'Conditions (Optional)')}</span>
        <button
          onClick={addCondition}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-all duration-200 font-medium"
        >
          <PlusIcon size={14} />
          {t('auto_add_condition', 'Add Condition')}
        </button>
      </div>

      <AnimatePresence>
        {conditions.map((condition, idx) => (
          <motion.div
            key={condition.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex gap-2 items-end"
          >
            {idx > 0 && <span className="text-gray-500 text-sm font-semibold px-2">{t('auto_and', 'AND')}</span>}
            <select
              value={condition.field}
              onChange={(e) => updateCondition(condition.id, { field: e.target.value })}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white"
            >
              <option value="status">{t('auto_status', 'Status')}</option>
              <option value="engagement">{t('auto_engagement', 'Engagement')}</option>
              <option value="followers">{t('auto_followers', 'Followers')}</option>
              <option value="timestamp">{t('auto_timestamp', 'Timestamp')}</option>
            </select>
            <select
              value={condition.operator}
              onChange={(e) => updateCondition(condition.id, { operator: e.target.value as OperatorType })}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white"
            >
              <option value="equals">{t('auto_equals', 'Equals')}</option>
              <option value="greater_than">&gt;</option>
              <option value="less_than">&lt;</option>
              <option value="contains">{t('auto_contains', 'Contains')}</option>
              <option value="not_equals">≠</option>
            </select>
            <input
              type="text"
              value={condition.value}
              onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white"
              placeholder={t('auto_value', 'Value')}
            />
            <button
              onClick={() => removeCondition(condition.id)}
              className="p-2 hover:bg-red-50 rounded-lg transition-all duration-200 text-gray-600 hover:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

// ==================== ActionBuilder ====================

interface ActionBuilderProps {
  actions: Action[];
  onChange: (actions: Action[]) => void;
  onEditAction: (action: Action) => void;
}

export const ActionBuilder: React.FC<ActionBuilderProps> = ({ actions, onChange, onEditAction }) => {
  const { t } = useTranslation();
  const [showActionPicker, setShowActionPicker] = useState(false);
  const actionConfig = useMemo(() => getActionConfig(t), [t]);

  const addAction = useCallback(
    (type: ActionType) => {
      onChange([
        ...actions,
        {
          id: Math.random().toString(36).slice(2),
          type,
          config: {},
          order: actions.length,
        },
      ]);
      setShowActionPicker(false);
    },
    [actions, onChange]
  );

  const removeAction = useCallback(
    (id: string) => {
      onChange(actions.filter((a) => a.id !== id));
    },
    [actions, onChange]
  );

  const reorderActions = useCallback(
    (from: number, to: number) => {
      const newActions = [...actions];
      const [removed] = newActions.splice(from, 1);
      newActions.splice(to, 0, removed);
      onChange(newActions.map((a, i) => ({ ...a, order: i })));
    },
    [actions, onChange]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="block text-sm font-semibold text-gray-900">{t('auto_actions', 'Actions')}</span>
        <button
          onClick={() => setShowActionPicker(!showActionPicker)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-all duration-200 font-medium"
        >
          <PlusIcon size={14} />
          {t('auto_add_action', 'Add Action')}
        </button>
      </div>

      <AnimatePresence>
        {showActionPicker && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid grid-cols-2 gap-2 pb-3"
          >
            {Object.entries(actionConfig).map(([key, config]) => (
              <motion.button
                key={key}
                onClick={() => addAction(key as ActionType)}
                className="flex flex-col items-center gap-1 p-3 border border-slate-200/60 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left"
                style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
              >
                <span className="text-emerald-600">{config.icon}</span>
                <span className="text-xs font-semibold text-gray-900">{config.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-2">
        <AnimatePresence>
          {actions.map((action, idx) => (
            <ActionItem
              key={action.id}
              action={action}
              index={idx}
              onRemove={removeAction}
              onReorder={reorderActions}
              onEdit={onEditAction}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
